import React, { useState, useEffect } from 'react';
import {
    Card,
    CardHeader,
    CardBody,
    Row,
    Col,
    Badge,
    Alert,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button
} from 'reactstrap';
import { FiBox, FiTag, FiDollarSign, FiImage, FiX, FiList, FiPackage, FiInfo } from 'react-icons/fi';
import axios from '../../../api/axios';

const ConsumedInventoryItemsPreview = ({ ticketId }) => {
    const [ticketData, setTicketData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageModal, setImageModal] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`v2/inventory/consume/ticket/${ticketId}/details`);
                if (response.data.success) {
                    setTicketData(response.data.data);
                } else {
                    setError('Failed to fetch ticket details');
                }
            } catch (err) {
                setError('Error fetching data: ' + err.message);
            } finally {
                setLoading(false);
            }
        };

        if (ticketId) {
            fetchData();
        }
    }, [ticketId]);

    const toggleImageModal = () => setImageModal(!imageModal);

    const openImageModal = (imageUrl, type) => {
        setSelectedImage({ url: imageUrl, type });
        setImageModal(true);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '20rem' }}>
                <div className="w-100" style={{ maxWidth: '1000px' }}>
                    <div className="placeholder-glow">
                        <div className="placeholder bg-secondary rounded mb-3" style={{ height: '3rem', width: '40%' }}></div>
                        <div className="placeholder bg-secondary rounded mb-2" style={{ height: '1.5rem', width: '100%' }}></div>
                        <div className="placeholder bg-secondary rounded mb-2" style={{ height: '1.5rem', width: '90%' }}></div>
                        <div className="placeholder bg-secondary rounded mb-2" style={{ height: '1.5rem', width: '85%' }}></div>
                        <div className="placeholder bg-secondary rounded" style={{ height: '10rem' }}></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '20rem' }}>
                <Alert color="danger" className="d-flex align-items-center shadow-sm border-0">
                    <FiX className="me-2 fs-5" />
                    <div className="me-3">{error}</div>
                    <Button
                        color="danger"
                        size="sm"
                        onClick={() => window.location.reload()}
                        className="ms-auto"
                    >
                        Retry
                    </Button>
                </Alert>
            </div>
        );
    }

    if (!ticketData) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '20rem' }}>
                <Alert color="warning" className="d-flex align-items-center shadow-sm border-0">
                    <FiInfo className="me-2 fs-5" />
                    No ticket data available
                </Alert>
            </div>
        );
    }

    const { ticket, consumed_items, summary } = ticketData;

    return (
        <div className="container-fluid py-4 px-3">
            <Card className="shadow-lg border-0 rounded-lg overflow-hidden">
                <CardHeader
                    className="text-white border-0 position-relative"
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        padding: '2rem'
                    }}
                >
                    <div className="d-flex align-items-center">
                        <div>
                            <h2 className="mb-1 fs-3 fw-bold text-white">Consumed Inventory Items</h2>
                            <p className="mb-0 text-white-50">Ticket ID: {ticketId}</p>
                        </div>
                    </div>
                </CardHeader>

                <CardBody className="p-0">
                    {consumed_items.length > 0 ? (
                        <div className="p-4">
                            {/* Items Layout */}
                            <div className="row g-4">
                                {consumed_items.map((item, index) => (
                                    <div key={item._id} className="col-12">
                                        <Card
                                            className="border-0 shadow-sm h-100 position-relative overflow-hidden"
                                            style={{
                                                transition: 'all 0.3s ease',
                                                borderLeft: '4px solid #667eea'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                                            }}
                                        >
                                            <CardBody className="p-4">
                                                {/* Information Row */}
                                                <Row className="align-items-center mb-3">
                                                    <Col lg="3">
                                                        <div className="d-flex align-items-center">
                                                            <div
                                                                className="me-3 d-flex align-items-center justify-content-center rounded-circle"
                                                                style={{
                                                                    width: '45px',
                                                                    height: '45px',
                                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                                }}
                                                            >
                                                                <FiPackage className="text-white fs-5" />
                                                            </div>
                                                            <div>
                                                                <h6 className="mb-1 fw-bold text-dark">
                                                                    {item.inventory_item_id.name}
                                                                </h6>
                                                                <small className="text-muted">
                                                                    {item.inventory_item_id.make}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </Col>

                                                    <Col lg="2">
                                                        <div className="text-center">
                                                            <Badge
                                                                className="px-3 py-2 fs-6 fw-semibold"
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                                                    border: 'none',
                                                                    color: 'white'
                                                                }}
                                                            >
                                                                <FiTag className="me-1" />
                                                                {item.inventory_item_id.modelNo}
                                                            </Badge>
                                                            <div className="small text-muted mt-1">Model No</div>
                                                        </div>
                                                    </Col>

                                                    <Col lg="2">
                                                        <div className="text-center">
                                                            <Badge
                                                                className="px-3 py-2 fs-5 fw-bold"
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                                                                    border: 'none',
                                                                    color: 'white'
                                                                }}
                                                            >
                                                                {item.consumed_quantity}
                                                            </Badge>
                                                            <div className="small text-muted mt-1">Quantity</div>
                                                        </div>
                                                    </Col>

                                                    <Col lg="2">
                                                        <div className="text-center">
                                                            <div className="fw-bold fs-5" style={{ color: '#28a745' }}>
                                                                <FiDollarSign className="me-1" />
                                                                {item.inventory_item_id.costPerUnit}
                                                            </div>
                                                            <div className="small text-muted">Cost/Unit</div>
                                                        </div>
                                                    </Col>

                                                    <Col lg="3">
                                                        <div>
                                                            <div className="small text-muted mb-1">Notes</div>
                                                            <div className="text-dark">
                                                                {item.notes || (
                                                                    <span className="text-muted fst-italic">No notes</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {/* Images Section - Now at bottom */}
                                                {(item.images.images_before.length > 0 || item.images.images_after.length > 0) && (
                                                    <div className="pt-3 border-top">
                                                        <div className="d-flex align-items-center mb-3">
                                                            <FiImage className="me-2 text-muted" />
                                                            <span className="fw-semibold text-muted">Images</span>
                                                        </div>
                                                        
                                                        <Row className="g-3">
                                                            {/* Before Images */}
                                                            {item.images.images_before.length > 0 && (
                                                                <Col md="6">
                                                                    <div className="mb-2">
                                                                        <Badge
                                                                            color="warning"
                                                                            className="mb-2 fw-semibold"
                                                                        >
                                                                            Before ({item.images.images_before.length})
                                                                        </Badge>
                                                                    </div>
                                                                    <div className="d-flex gap-2 flex-wrap">
                                                                        {item.images.images_before.map((img, idx) => (
                                                                            <div
                                                                                key={idx}
                                                                                className="position-relative"
                                                                                style={{
                                                                                    width: '60px',
                                                                                    height: '60px',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                                onClick={() => openImageModal(img.signed_url, 'Before')}
                                                                            >
                                                                                <img
                                                                                    src={img.signed_url}
                                                                                    alt={`Before ${idx + 1}`}
                                                                                    className="rounded border w-100 h-100"
                                                                                    style={{
                                                                                        objectFit: 'cover',
                                                                                        transition: 'transform 0.2s'
                                                                                    }}
                                                                                    onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                                                                                    onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                                                                                />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </Col>
                                                            )}

                                                            {/* After Images */}
                                                            {item.images.images_after.length > 0 && (
                                                                <Col md="6">
                                                                    <div className="mb-2">
                                                                        <Badge
                                                                            color="success"
                                                                            className="mb-2 fw-semibold"
                                                                        >
                                                                            After ({item.images.images_after.length})
                                                                        </Badge>
                                                                    </div>
                                                                    <div className="d-flex gap-2 flex-wrap">
                                                                        {item.images.images_after.map((img, idx) => (
                                                                            <div
                                                                                key={idx}
                                                                                className="position-relative"
                                                                                style={{
                                                                                    width: '60px',
                                                                                    height: '60px',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                                onClick={() => openImageModal(img.signed_url, 'After')}
                                                                            >
                                                                                <img
                                                                                    src={img.signed_url}
                                                                                    alt={`After ${idx + 1}`}
                                                                                    className="rounded border w-100 h-100"
                                                                                    style={{
                                                                                        objectFit: 'cover',
                                                                                        transition: 'transform 0.2s'
                                                                                    }}
                                                                                    onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                                                                                    onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                                                                                />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </Col>
                                                            )}
                                                        </Row>
                                                    </div>
                                                )}

                                                {/* No Images State */}
                                                {item.images.images_before.length === 0 && item.images.images_after.length === 0 && (
                                                    <div className="pt-3 border-top">
                                                        <div className="d-flex align-items-center text-muted">
                                                            <FiImage className="me-2" />
                                                            <span className="small">No images available</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </CardBody>
                                        </Card>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="p-5 text-center">
                            <div
                                className="mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle bg-light"
                                style={{ width: '80px', height: '80px' }}
                            >
                                <FiBox className="fs-1 text-muted" />
                            </div>
                            <h5 className="text-muted mb-3">No Consumed Items Found</h5>
                            <p className="text-muted">There are no consumed items for this ticket yet.</p>
                        </div>
                    )}

                    {/* Compact Summary Section */}
                    {consumed_items.length > 0 && (
                        <div
                            className="p-3"
                            style={{
                                background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%)',
                                borderTop: '1px solid #e9ecef'
                            }}
                        >
                            <div className="d-flex align-items-center mb-3">
                                <div
                                    className="me-2 d-flex align-items-center justify-content-center rounded-circle"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    }}
                                >
                                    <FiList className="text-white fs-6" />
                                </div>
                                <h6 className="mb-0 fw-bold text-dark">Summary Overview</h6>
                            </div>

                            <Row className="g-3">
                                <Col md="4">
                                    <div className="d-flex align-items-center p-2 bg-white rounded border-start border-primary border-3">
                                        <div
                                            className="me-3 d-flex align-items-center justify-content-center rounded-circle"
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                            }}
                                        >
                                            <FiBox className="text-white fs-6" />
                                        </div>
                                        <div>
                                            <div className="fs-4 fw-bold text-dark mb-0">
                                                {summary.total_items_consumed}
                                            </div>
                                            <small className="text-muted">Total Items</small>
                                        </div>
                                    </div>
                                </Col>

                                <Col md="4">
                                    <div className="d-flex align-items-center p-2 bg-white rounded border-start border-warning border-3">
                                        <div
                                            className="me-3 d-flex align-items-center justify-content-center rounded-circle"
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
                                            }}
                                        >
                                            <FiPackage className="text-white fs-6" />
                                        </div>
                                        <div>
                                            <div className="fs-4 fw-bold text-dark mb-0">
                                                {summary.total_quantity_consumed}
                                            </div>
                                            <small className="text-muted">Total Quantity</small>
                                        </div>
                                    </div>
                                </Col>

                                <Col md="4">
                                    <div className="d-flex align-items-center p-2 bg-white rounded border-start border-info border-3">
                                        <div
                                            className="me-3 d-flex align-items-center justify-content-center rounded-circle"
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                                            }}
                                        >
                                            <FiList className="text-white fs-6" />
                                        </div>
                                        <div>
                                            <div className="fs-4 fw-bold text-dark mb-0">
                                                {summary.unique_inventory_items}
                                            </div>
                                            <small className="text-muted">Unique Items</small>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Enhanced Image Modal */}
            <Modal
                isOpen={imageModal}
                toggle={toggleImageModal}
                size="lg"
                centered
                contentClassName="shadow-lg rounded-lg border-0"
            >
                <ModalHeader
                    toggle={toggleImageModal}
                    className="border-0 mb-3"
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}
                >
                    <span className="fw-bold text-white fs-5">
                        <FiImage className="me-2" />
                        {selectedImage?.type} Image
                    </span>
                </ModalHeader>
                <ModalBody className="text-center p-4">
                    {selectedImage && (
                        <div className="position-relative">
                            <img
                                src={selectedImage.url}
                                alt={`${selectedImage.type} view`}
                                className="img-fluid rounded shadow-sm"
                                style={{
                                    maxHeight: '70vh',
                                    width: 'auto',
                                    border: '3px solid #f8f9fa'
                                }}
                            />
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="border-0 pt-0">
                    <Button
                        color="secondary"
                        onClick={toggleImageModal}
                        className="px-4 py-2 fw-semibold"
                        style={{ borderRadius: '25px' }}
                    >
                        <FiX className="me-2" />
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default ConsumedInventoryItemsPreview